import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Auth } from '@angular/fire/auth';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import {
  EMAIL_TEMPLATE_CATALOG,
  EMAIL_TEMPLATE_TOKEN_HINTS,
  EmailTemplateCatalogEntry,
  EmailTemplateDocId,
} from '../../../models/email-template.model';
import { EmailTemplateAdminService } from '../../../services/email-template-admin.service';
import { AdminEmailService } from '../../../services/admin-email.service';

@Component({
  selector: 'app-admin-email-templates',
  standalone: true,
  imports: [FormsModule, RouterLink, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './admin-email-templates.component.html',
  styleUrls: ['./admin-email-templates.component.scss'],
})
export class AdminEmailTemplatesComponent implements OnInit {
  private readonly templatesApi = inject(EmailTemplateAdminService);
  private readonly adminEmail   = inject(AdminEmailService);
  private readonly auth         = inject(Auth);

  readonly catalog = EMAIL_TEMPLATE_CATALOG;
  readonly tokenHints = EMAIL_TEMPLATE_TOKEN_HINTS;

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly seeding = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly saveError = signal<string | null>(null);
  readonly saveSuccess = signal<string | null>(null);

  readonly selectedDocId = signal<EmailTemplateDocId>('PENDING_FORMALIZATION');
  readonly subject = signal('');
  readonly bodyHtml = signal('');
  readonly bodyText = signal('');
  readonly enabled = signal(true);
  readonly description = signal('');
  readonly audience = signal<'guest' | 'owner' | 'none'>('guest');

  readonly emailTestTo      = signal('');
  readonly emailTestSending = signal(false);
  readonly emailTestSuccess = signal<string | null>(null);
  readonly emailTestError   = signal<string | null>(null);

  readonly pushTestSending = signal(false);
  readonly pushTestSuccess = signal<string | null>(null);
  readonly pushTestError   = signal<string | null>(null);

  async ngOnInit(): Promise<void> {
    this.emailTestTo.set(this.auth.currentUser?.email?.trim() ?? '');
    await this.selectDoc('PENDING_FORMALIZATION');
    this.loading.set(false);
  }

  async selectDoc(docId: EmailTemplateDocId): Promise<void> {
    this.selectedDocId.set(docId);
    this.saveSuccess.set(null);
    this.saveError.set(null);
    const entry = this.catalogEntry(docId);
    this.audience.set(entry?.audience ?? 'guest');
    this.loading.set(true);
    this.loadError.set(null);
    try {
      const t = await this.templatesApi.getTemplate(docId);
      this.subject.set(t.subject);
      this.bodyHtml.set(t.bodyHtml);
      this.bodyText.set(t.bodyText ?? '');
      this.enabled.set(t.enabled);
      this.description.set(t.description ?? '');
      this.audience.set(t.audience ?? entry?.audience ?? 'guest');
    } catch (err: unknown) {
      this.loadError.set((err as { message?: string })?.message ?? 'No se pudo cargar la plantilla.');
    } finally {
      this.loading.set(false);
    }
  }

  catalogEntry(docId: EmailTemplateDocId): EmailTemplateCatalogEntry | undefined {
    return this.catalog.find(e => e.docId === docId);
  }

  async save(): Promise<void> {
    if (this.saving()) return;
    this.saving.set(true);
    this.saveError.set(null);
    this.saveSuccess.set(null);
    try {
      await this.templatesApi.updateTemplate(
        this.selectedDocId(),
        this.subject(),
        this.bodyHtml(),
        { bodyText: this.bodyText(), enabled: this.enabled() },
      );
      this.saveSuccess.set('Plantilla guardada. Los próximos correos usarán este contenido.');
    } catch (err: unknown) {
      this.saveError.set((err as { message?: string })?.message ?? 'No se pudo guardar.');
    } finally {
      this.saving.set(false);
    }
  }

  async sendTestEmail(): Promise<void> {
    if (this.emailTestSending()) return;
    this.emailTestSending.set(true);
    this.emailTestSuccess.set(null);
    this.emailTestError.set(null);
    try {
      const to  = this.emailTestTo().trim();
      const res = await this.adminEmail.sendTestEmail(to || undefined);
      const idPart = res.messageId ? ` (id: ${res.messageId})` : '';
      this.emailTestSuccess.set(`Correo enviado a ${res.to}${idPart}. Origen: ${res.appOrigin}`);
    } catch (err: unknown) {
      const fe  = err as { code?: string; message?: string };
      const msg = fe.message?.trim()
        || (fe.code === 'functions/permission-denied'
          ? 'Tu cuenta no tiene permiso de administrador.'
          : 'No se pudo enviar el correo de prueba.');
      this.emailTestError.set(msg);
    } finally {
      this.emailTestSending.set(false);
    }
  }

  async sendTestPush(): Promise<void> {
    if (this.pushTestSending()) return;
    this.pushTestSending.set(true);
    this.pushTestSuccess.set(null);
    this.pushTestError.set(null);
    try {
      const res = await this.adminEmail.sendTestPush();
      if (res.sentCount === 0) {
        this.pushTestSuccess.set(
          `Ningún admin tiene token FCM registrado aún (${res.adminCount} admins encontrados). ` +
          `Abre la app móvil y acepta los permisos de notificación.`,
        );
      } else {
        this.pushTestSuccess.set(
          `Push enviado a ${res.sentCount} dispositivo(s) de ${res.adminCount} admin(s).` +
          (res.skippedNoToken > 0 ? ` (${res.skippedNoToken} sin token registrado)` : ''),
        );
      }
    } catch (err: unknown) {
      const fe  = err as { code?: string; message?: string };
      const msg = fe.message?.trim()
        || (fe.code === 'functions/permission-denied'
          ? 'Tu cuenta no tiene permiso de administrador.'
          : 'No se pudo enviar el push de prueba.');
      this.pushTestError.set(msg);
    } finally {
      this.pushTestSending.set(false);
    }
  }

  async seedDefaults(): Promise<void> {
    if (this.seeding()) return;
    this.seeding.set(true);
    this.saveError.set(null);
    try {
      const res = await this.templatesApi.seedDefaults(false);
      this.saveSuccess.set(`Plantillas por defecto: ${res.written} creadas, ${res.skipped} ya existían.`);
      await this.selectDoc(this.selectedDocId());
    } catch (err: unknown) {
      this.saveError.set((err as { message?: string })?.message ?? 'No se pudieron crear las plantillas.');
    } finally {
      this.seeding.set(false);
    }
  }
}
